// Budget pieces shared by the Budgets tab and its tiles (SPEC §8.6).
import { useQuery } from "@tanstack/react-query";
import { useState, type CSSProperties, type ReactNode } from "react";
import { changeAmount, formatBudgetPeriod, formatDate, formatUsdFull, formatWibLong, isOverBudget, parseBudgetPeriod, type BudgetChange, type ChangeWindow, type Quarter } from "@po/shared";
import { api, qs, type BudgetsPage, type Freshness } from "../lib/api";
import { ago } from "../lib/format";
import { useDashboard } from "../pages/Dashboard";
import { useNow } from "./Sidebar";
import { todayWib } from "./PageHeader";

export const BUDGET_CAPTION = "USD, at PO rate · actual is committed + accrued, not invoiced";

/** The Budgets page for the period in the URL, on the Vessel filter only. */
export function useBudgetsPage() {
  const { search, filters, me, freshness } = useDashboard();
  const { period, forecasts } = search;
  const synced = !!freshness?.budgets.asOf;
  return useQuery({
    queryKey: ["budgets/page", filters.vessel ?? [], period ?? null, forecasts ?? null],
    queryFn: () => api<BudgetsPage>(`/budgets/page${qs({ vessel: filters.vessel, period, forecasts })}`),
    enabled: me.canSeeBudgets && synced,
  });
}

export const usd = (n: number | null | undefined) => (n == null ? "—" : formatUsdFull(n));

/** "% used", or "budget $0" when spend lands on a zero budget (SPEC §3.2). */
export function usedLabel(budgetUsd: number, usedUsd: number): string {
  if (budgetUsd > 0) return `${Math.round((usedUsd / budgetUsd) * 100)} %`;
  return usedUsd > 0 ? "budget $0" : "—";
}

const overOf = (budgetUsd: number, usedUsd: number) => isOverBudget(budgetUsd, { committedUsd: usedUsd, accruedUsd: 0 });

/** Used bar: spend (committed + accrued) against the budget; red past it, with a tick at the budget. */
export function UsedBar({ budgetUsd, spendUsd, width = 110 }: { budgetUsd: number | null; spendUsd: number; width?: number }) {
  if (budgetUsd === null) return <span className="text-[11.5px] text-muted">not comparable</span>;
  const used = spendUsd;
  const scale = Math.max(budgetUsd, used) || 1;
  const over = overOf(budgetUsd, used);
  const close = !over && budgetUsd > 0 && used / budgetUsd >= 0.85;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="relative inline-block h-[7px] rounded-full overflow-hidden shrink-0" style={{ width, background: "var(--budget-track)" }} aria-hidden>
        <span className="absolute inset-y-0 left-0" style={{ width: `${(used / scale) * 100}%`, background: over ? "var(--budget-over)" : "var(--budget-committed)" }} />
        {over && budgetUsd > 0 && <span className="absolute inset-y-0 w-[2px]" style={{ left: `${(budgetUsd / scale) * 100}%`, background: "var(--ink)" }} />}
      </span>
      <span className={`text-[11.5px] w-[64px] text-left ${over ? "neg" : close ? "font-semibold" : "text-muted"}`} style={close ? { color: "var(--budget-warn)" } : undefined}>
        {usedLabel(budgetUsd, used)}
      </span>
    </span>
  );
}

/** The Over budget flag: an outlined chip like the other flags (SPEC §3.2, §14). */
export function OverChip({ title }: { title?: string } = {}) {
  return (
    <span className="flag" style={{ "--c": "var(--flag-over_budget)" } as CSSProperties} title={title}>
      Over budget
    </span>
  );
}

export function OverCount({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span className="text-[11px] font-semibold whitespace-nowrap" style={{ color: "var(--flag-over_budget)" }}>
      {n} over
    </span>
  );
}

/** "Budgets as of", beside the period picker, with §11.2's levels; severely stale shows red here only. */
export function BudgetsAsOf({ f }: { f?: Freshness }) {
  const now = useNow();
  if (!f?.budgets.asOf) return null;
  const at = new Date(f.budgets.asOf);
  const rel = ago(now - at.getTime());
  const level = f.budgets.level;
  if (level === "fresh")
    return (
      <span className="text-[12px] text-muted" title={formatWibLong(at)} tabIndex={0}>
        Budgets as of {rel}
      </span>
    );
  return (
    <span className="text-[12px] font-semibold" style={{ color: level === "stale" ? "var(--budget-warn)" : "var(--budget-over)" }} title={formatWibLong(at)} tabIndex={0}>
      <span aria-hidden>⚠ </span>Budgets may be out of date — last updated {rel}
    </span>
  );
}

/** Quarter / Year, then the four quarters with the current one marked; the budget years, each marked closed or current. */
export function PeriodPicker({ years }: { years: number[] }) {
  const { search, setSearch } = useDashboard();
  const today = todayWib();
  const p = parseBudgetPeriod(search.period, today);
  const current = parseBudgetPeriod(undefined, today);
  const set = (next: { year: number; quarter: Quarter | null }) => setSearch({ period: formatBudgetPeriod(next) });
  const shownYears = years.includes(p.year) ? years : [...years, p.year].sort((a, b) => b - a);
  return (
    <span className="inline-flex items-center gap-2 flex-wrap">
      <span className="seg" role="group" aria-label="Period">
        <button aria-pressed={p.quarter !== null} onClick={() => set({ year: p.year, quarter: p.quarter ?? (p.year === current.year ? current.quarter : 1) })}>
          Quarter
        </button>
        <button aria-pressed={p.quarter === null} onClick={() => set({ year: p.year, quarter: null })}>
          Year
        </button>
      </span>
      {p.quarter !== null && (
        <span className="seg" role="group" aria-label="Quarter">
          {([1, 2, 3, 4] as const).map((q) => (
            <button key={q} aria-pressed={p.quarter === q} onClick={() => set({ year: p.year, quarter: q })}>
              Q{q}
              {p.year === current.year && q === current.quarter && <span className="opacity-75"> · now</span>}
            </button>
          ))}
        </span>
      )}
      <label className="inline-flex items-center gap-1.5 text-[12px] text-muted">
        Budget period
        <select className="input text-[12.5px]" aria-label="Budget year" value={p.year} onChange={(e) => set({ year: Number(e.target.value), quarter: p.quarter })}>
          {shownYears.map((y) => (
            <option key={y} value={y}>
              Year {y}
              {y < current.year ? " · closed" : y === current.year ? " · current" : ""}
            </option>
          ))}
        </select>
      </label>
    </span>
  );
}

/**
 * The closed-year mark beside the period picker (SPEC §8.6): a year before the current one, whose
 * budgets Sertica has retired.
 */
export function ClosedYear({ page }: { page: BudgetsPage }) {
  if (!page.closed) return null;
  return (
    <span className="badge" style={{ "--c": "var(--muted)" } as CSSProperties} title="A past budget year: its budgets are Retired in Sertica and nothing in it is flagged">
      Year {page.period.year} · closed{page.retiredOn ? ` · budgets retired ${formatDate(page.retiredOn)}` : ""}
    </span>
  );
}

/** "Use forecasts", as in Sertica's Financial Follow-up: on by default (SPEC §4.3 item 5). */
export function ForecastToggle() {
  const { search, setSearch } = useDashboard();
  const on = search.forecasts !== "0";
  return (
    <label className="inline-flex items-center gap-1.5 text-[12.5px]" title="Budget figures from each vessel's current forecast in Sertica, as Sertica's Financial Follow-up shows them with Use Forecasts ticked">
      <input type="checkbox" checked={on} onChange={() => setSearch({ forecasts: on ? "0" : undefined })} />
      Use forecasts
    </label>
  );
}

/**
 * The budget card in place of the KPI row (SPEC §8.6): spend against the unit budgets and SEA's and
 * PEA's, the budget split into Sertica's base, what finance's forecast notes added, and SEA/PEA's,
 * and a marker at the spend. It follows the period and the vessel filter.
 */
export function BudgetTiles() {
  const { filters } = useDashboard();
  const { data, isFetching } = useBudgetsPage();
  if (!data) return <section aria-label="Budget figures" className="card h-[150px] animate-pulse" />;
  const t = data.followUp.totals;
  // SEA's and PEA's budgets of the same vessels, and of those Sertica has none for, add up with Sertica's.
  const ac = data.autoCount.vessels;
  const acBudget = ac.reduce((n, v) => n + v.netBudgetUsd, 0);
  const acSpend = ac.reduce((n, v) => n + v.actualUsd, 0);
  const serticaBudget = t.unitBudgets.budgetUsd;
  const budget = serticaBudget + acBudget;
  const spend = t.unitBudgets.committedUsd + t.unitBudgets.accruedUsd + acSpend;
  const remaining = budget - spend;
  const added = data.noteAddedUsd;
  const base = serticaBudget - added;
  const overCount = t.overCount + ac.reduce((n, v) => n + v.overCount, 0);
  const lineCount = t.lineCount + ac.reduce((n, v) => n + v.lineCount, 0);
  const vessels = filters.vessel ?? [];
  const scope = vessels.length === 1 ? vessels[0]! : vessels.length ? `${vessels.length} vessels` : "All vessels";
  const period = data.period.quarter === null ? `Year ${data.period.year}` : `Q${data.period.quarter} ${data.period.year}`;
  const over = overOf(budget, spend);
  const close = !over && budget > 0 && spend / budget >= 0.85;
  const status = over
    ? { text: "Over budget", c: "var(--budget-over)" }
    : close
      ? { text: "Close to budget", c: "var(--budget-warn)" }
      : { text: "Under budget", c: "var(--budget-added)" };
  // Base, then added (or the deducted part of the base, hatched), then SEA/PEA's; spend past them runs on in red.
  const scale = Math.max(base + acBudget, budget, spend) || 1;
  const pct = (n: number) => `${(Math.max(n, 0) / scale) * 100}%`;
  const segments = [
    { w: Math.min(base, serticaBudget), bg: "var(--budget-base)" },
    { w: added > 0 ? added : 0, bg: "var(--budget-additional)" },
    { w: added < 0 ? -added : 0, bg: "repeating-linear-gradient(135deg, var(--budget-track) 0 4px, transparent 4px 8px)" },
    { w: acBudget, bg: "var(--budget-autocount)" },
    { w: spend > budget ? spend - Math.max(budget, base + acBudget) : 0, bg: "var(--budget-over)" },
  ];
  return (
    <section aria-label="Budget figures" className={`card p-4 md:p-5 space-y-3 ${isFetching ? "opacity-80" : ""}`}>
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
        <div className="min-w-0">
          <div className="label truncate">
            {scope} · {period}
          </div>
          <div className="flex flex-wrap items-baseline gap-x-2 mt-1">
            <span className={`text-[32px] font-extrabold leading-none tnum ${over ? "neg" : ""}`}>{formatUsdWhole(spend)}</span>
            <span className="text-[14px] text-muted tnum">actual of {formatUsdWhole(budget)}</span>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1.5">
          {budget > 0 || spend > 0 ? (
            <span className="badge text-[12.5px] px-2.5 py-0.5 rounded-full" style={{ "--c": status.c } as CSSProperties}>
              {status.text}
            </span>
          ) : null}
          <span className="text-[13px] text-muted tnum">
            {budget > 0 ? `${((spend / budget) * 100).toFixed(1)}% used · ` : budget === 0 && spend > 0 ? "no budget · " : ""}
            <b style={{ color: remaining < 0 ? "var(--budget-over)" : "var(--budget-added)" }}>{formatUsdWhole(Math.abs(remaining))}</b> {remaining < 0 ? "over" : "remaining"}
          </span>
        </div>
      </div>
      <div className="relative h-[22px] rounded-md overflow-hidden flex" style={{ background: "var(--budget-track)" }} role="img" aria-label={`${formatUsdWhole(spend)} actual of ${formatUsdWhole(budget)}`}>
        {segments.map((x, i) => x.w > 0 && <span key={i} className="h-full shrink-0" style={{ width: pct(x.w), background: x.bg }} />)}
        {spend > 0 && <span className="absolute inset-y-0 w-[3px] -ml-[1.5px]" style={{ left: pct(spend), background: "var(--budget-spend)" }} />}
      </div>
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[12.5px] text-ink2 tnum">
        <Key bg="var(--budget-base)">Base {formatUsdWhole(base)}</Key>
        {added > 0 && <Key bg="var(--budget-additional)">Additional +{formatUsdWhole(added)}</Key>}
        {added < 0 && <Key bg="repeating-linear-gradient(135deg, var(--muted) 0 2px, transparent 2px 4px)">Deducted −{formatUsdWhole(-added)}</Key>}
        {ac.length > 0 && <Key bg="var(--budget-autocount)">SEA / PEA {formatUsdWhole(acBudget)}</Key>}
        <Key bg="var(--budget-spend)" bar>
          Actual
        </Key>
        {data.closed && spend > 0 && <ClosedSplit received={t.unitBudgets.accruedUsd} open={t.unitBudgets.committedUsd} year={data.period.year} />}
        <span className="flex-1" />
        <span className={overCount ? "neg text-[12.5px]" : "text-muted"}>
          {overCount} of {lineCount} lines over budget
        </span>
      </div>
      <div className="text-[10.5px] text-muted">
        {BUDGET_CAPTION} · additional is the net of finance&apos;s forecast notes, shown when budgets use forecasts
        {ac.length > 0 && " · SEA / PEA is their budgets net of transfers out, their actual their own POs"}
      </div>
    </section>
  );
}

/**
 * A closed year's actual split (SPEC §8.6): received (accrued), and still open (committed), which a
 * year later mostly means a receipt never recorded. "Still open" opens the PO list on that year's
 * open POs.
 */
function ClosedSplit({ received, open, year }: { received: number; open: number; year: number }) {
  const { setSearch } = useDashboard();
  return (
    <span className="inline-flex flex-wrap items-center gap-x-3">
      <span>Received {formatUsdWhole(received)}</span>
      <button
        className="text-brand font-semibold hover:underline"
        title={`Ordered or Partly received POs created in ${year}: still committed, most likely a receipt not recorded in Sertica`}
        onClick={() => setSearch({ tab: "pos", poStage: ["ordered", "partly_received"], from: `${year}-01-01`, to: `${year}-12-31` })}
      >
        Still open {formatUsdWhole(open)} ›
      </button>
    </span>
  );
}

const formatUsdWhole = (n: number) => `${n < 0 ? "−" : ""}$${Math.round(Math.abs(n)).toLocaleString("en-US")}`;

function Key({ bg, bar = false, children }: { bg: string; bar?: boolean; children: ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span aria-hidden className={bar ? "inline-block w-[3px] h-[12px]" : "inline-block w-[10px] h-[10px] rounded-sm"} style={{ background: bg }} />
      {children}
    </span>
  );
}

/** A change in USD with its sign: "+$200.00", "−$50.00". */
export const signed = (n: number) => `${n > 0 ? "+" : n < 0 ? "−" : ""}${formatUsdFull(Math.abs(n))}`;

/** The changes on one budget line, with the window each happened in. */
export type LineChanges = { change: BudgetChange; window: ChangeWindow }[];

export const changeKey = (budgetNo: string | null, purchaseTypeNo: string | null, technicalAccountNo: string | null) =>
  JSON.stringify([budgetNo, purchaseTypeNo, technicalAccountNo]);

/** Changes by line, for the ⇄ marker. */
export function indexChanges(windows: ChangeWindow[]): Map<string, LineChanges> {
  const m = new Map<string, LineChanges>();
  for (const window of windows)
    for (const change of window.changes) {
      const k = changeKey(change.budgetNo, change.purchaseTypeNo, change.technicalAccountNo);
      m.set(k, [...(m.get(k) ?? []), { change, window }]);
    }
  return m;
}

export const windowLabel = (w: ChangeWindow) =>
  w.fromAt ? `between the syncs at ${formatWibLong(new Date(w.fromAt))} and ${formatWibLong(new Date(w.toAt))}` : `at the sync at ${formatWibLong(new Date(w.toAt))}`;

export const SERTICA_COMMENTS = "Finance's reason is in Sertica's follow-up comments, which the dashboard cannot read: Sertica › Budget › Budget follow-up.";

/** The ⇄ changed marker on a line, with a popover of its changes (SPEC §8.6). */
export function ChangeMark({ changes }: { changes?: LineChanges }) {
  const [open, setOpen] = useState(false);
  if (!changes?.length) return null;
  return (
    <span className="relative inline-block" onClick={(e) => e.stopPropagation()}>
      <button title="Budget changed" aria-expanded={open} onClick={() => setOpen((v) => !v)} className="text-[11px] px-1 rounded border border-line text-brand font-normal not-italic">
        ⇄ changed
      </button>
      {open && (
        <div role="dialog" aria-label="Budget changes on this line" className="absolute z-30 left-0 top-6 w-[320px] max-w-[80vw] card p-2.5 shadow-lg text-left whitespace-normal font-normal not-italic text-ink">
          <div className="flex items-center">
            <span className="font-semibold text-[12.5px] flex-1">Budget changes on this line</span>
            <button aria-label="Close" className="text-muted px-1" onClick={() => setOpen(false)}>
              ✕
            </button>
          </div>
          {changes.map(({ change: c, window: w }) => (
            <div key={c.id} className="text-[12px] py-1 border-t border-line mt-1">
              <div className="tnum">
                Year total {usd(c.oldTotalUsd)} → <b>{usd(c.newTotalUsd)}</b> ({signed(changeAmount(c))})
              </div>
              <div className="text-muted">Changed {windowLabel(w)}</div>
              {w.changes
                .filter((o) => o.id !== c.id && o.unitNo === c.unitNo)
                .map((o) => (
                  <div key={o.id} className="text-muted">
                    Same window: {o.purchaseTypeNo} · {o.technicalAccountNo} {signed(changeAmount(o))}
                  </div>
                ))}
            </div>
          ))}
          <div className="text-[11px] text-muted mt-1 border-t border-line pt-1">{SERTICA_COMMENTS}</div>
        </div>
      )}
    </span>
  );
}

/** Where to find a budget in Sertica, with Copy; there is no deep link. */
export function SerticaPath({ budgetNo }: { budgetNo: string }) {
  const text = `Sertica › Budget › Budget follow-up › ${budgetNo}`;
  const [done, setDone] = useState(false);
  return (
    <div className="text-[11.5px] text-muted flex items-center gap-1.5 flex-wrap">
      <span>Follow-up comments are in</span>
      <code className="px-1 rounded bg-hover text-ink2">{text}</code>
      <button
        className="text-brand underline"
        onClick={() => {
          void navigator.clipboard?.writeText(text);
          setDone(true);
        }}
      >
        {done ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
